/**
 * Spanish translations
 */

import type { TranslationKeys } from './types';

export const es: TranslationKeys = {
  // Navigation
  nav: {
    home: 'Inicio',
    about: 'Sobre mí',
    services: 'Servicios',
    projects: 'Proyectos',
    experience: 'Experiencia',
    playground: 'Playground',
    contact: 'Contacto',
    closeMenu: 'Cerrar menú',
    openMenu: 'Abrir menú',
  },

  // Hero Section
  hero: {
    subtitle: 'CTO & Tech Lead\nAI Full Stack Developer\nXR Developer',
    tagline: 'Especializado en',
    yearsExperience: 'años de experiencia',
    contactMe: 'Contáctame',
    viewProjects: 'Ver Proyectos',
    downloadCV: 'Download CV',
    scroll: 'Scroll',
  },

  // About Section
  about: {
    greeting: '¡Hola!',
    role: 'CTO & Tech Lead\nAI Full Stack Developer\nXR Developer',
    intro: {
      highlight:
        'Desarrollador <strong>AI full-stack</strong> y <strong>CTO</strong> con más de <span class="tech">9 años de experiencia</span> liderando proyectos tecnológicos innovadores.',
      specialization:
        'Especializado en <span class="tech">Unity3D</span>, <span class="tech">XR (AR/VR/MR)</span>, <span class="tech">configuradores 3D</span>, <span class="tech">IA</span> y <span class="tech">React/TypeScript</span>.',
    },
    cards: {
      leadership: {
        title: 'Liderazgo tecnológico',
        content: `<strong>CTO de HausLook</strong>, lidero el desarrollo de plataformas que combinan <strong>full-stack web</strong> (React/TypeScript/Supabase), <strong>configuradores 3D</strong> interactivos en Unity WebGL, y <strong>renderizado hiperrealista</strong> con 3ds Max y V-Ray. Integro <em>IA estratégicamente</em> para optimizar procesos y crear agentes inteligentes. Como <strong>Founder, CEO y CTO de Lyceum VR</strong>, he liderado <strong>consultoría tecnológica</strong> para clientes internacionales y dirigido <strong>equipos multidisciplinares</strong> (técnicos y creativos) en proyectos XR.`,
      },
      experience: {
        title: 'Experiencia diversa',
        content: `Mi trayectoria abarca desde <strong>experiencias inmersivas XR</strong> (AR/VR/MR) hasta <strong>arquitectura de software</strong> compleja. He <strong>fundado empresas</strong> (Lyceum VR), <strong>liderado equipos técnicos</strong>, y desarrollado desde <em>aplicaciones educativas</em> hasta <em>software de simulación militar</em>. Me apasiona el <strong>I+D</strong>, estar al día del <strong>estado del arte</strong> en tecnología puntera, <em>investigar e implementar</em> las últimas innovaciones.`,
      },
      aiDevelopment: {
        title: 'Desarrollo asistido por IA',
        content: `<strong>Pionero</strong> en el uso de herramientas de desarrollo asistido por IA como <strong>Claude Code</strong> y <strong>Cursor</strong>. Mi stack web (<strong>React/TypeScript/Supabase</strong>) es <em>100% AI-Powered</em>, maximizando productividad y calidad. En <strong>Unity/C#</strong> combino <em>9+ años de experiencia tradicional</em> con herramientas IA, aprovechando lo mejor de ambos mundos para proyectos XR de alta complejidad.`,
      },
    },
    techStack: 'Stack Tecnológico',
    techCategories: {
      unityXr: 'Unity & XR',
      unityXrBadge: '9+ años tradicional + AI',
      fullStackWeb: 'Full Stack Web',
      fullStackBadge: 'AI-Powered',
      rendering3d: '3D & Rendering',
      aiTools: 'Herramientas IA',
      devTools: 'Tools & Version Control',
      devOps: 'DevOps & Cloud',
    },
  },

  // What I Do Section
  whatIDo: {
    title: 'Donde la tecnología se encuentra con la creatividad',
    subtitle:
      'No solo creo software, diseño experiencias interactivas. Mi misión es transformar ideas en soluciones digitales inmersivas que sean intuitivas, atractivas y memorables.',
    services: {
      ctoLeadership: {
        title: 'CTO & Tech Leadership',
        description:
          'Liderazgo técnico y consultoría estratégica para startups y empresas tecnológicas.',
      },
      fullstack: {
        title: 'Desarrollo Full Stack',
        description:
          'Aplicaciones web modernas con React, TypeScript, Supabase y arquitectura escalable.',
      },
      configurators3d: {
        title: 'Configuradores 3D',
        description:
          'Configuradores interactivos WebGL y soluciones de visualización hiperrealista.',
      },
      xrDevelopment: {
        title: 'Desarrollo XR (AR/VR/MR)',
        description:
          'Experiencias inmersivas de realidad extendida para múltiples plataformas.',
      },
      aiIntegration: {
        title: 'Integración de IA',
        description:
          'Agentes inteligentes, automatización con IA y desarrollo asistido por IA.',
      },
      seriousGames: {
        title: 'Serious Games',
        description: 'Videojuegos con propósito educativo o empresarial.',
      },
      architecture: {
        title: 'Arquitectura de Software',
        description:
          'Diseño de sistemas escalables, mantenibles y de alto rendimiento.',
      },
      mobile: {
        title: 'iOS/Android',
        description: 'Desarrollo de aplicaciones móviles nativas y cross-platform.',
      },
    },
    scrollingBanner: {
      appDev: 'Desarrollo de Apps',
      creativeDesign: 'Diseño Creativo',
      softwareArch: 'Arquitectura de Software',
      databases: 'Bases de datos',
      videogames: 'Videojuegos',
    },
  },

  // Portfolio Section
  portfolio: {
    title: 'Descubre algunos de los trabajos en los que he participado',
    viewAll: 'Ver todos los proyectos',
    viewDetails: 'Ver Detalles',
  },

  // Experience Section
  experience: {
    title: 'Soy un desarrollador apasionado por la última tecnología',
    myExperience: 'Mi Experiencia',
    myEducation: 'Mi Educación',
    founder: 'Fundador',
    partner: 'Socio',
    currently: 'Actualmente',
  },

  // Contact Section
  contact: {
    title: 'Tu próximo proyecto empieza aquí.',
    emailMe: 'Escríbeme',
    callMe: 'Llámame',
    question: '¿Tienes un proyecto en mente?',
    helpAs: 'Puedo ayudarte como:',
    roles: {
      techLead: 'Tech Lead',
      cto: 'CTO',
      xrDev: 'Desarrollador XR',
      aiDev: 'Desarrollador IA',
      webDev: 'Desarrollador Web',
      frontend: 'Frontend',
      backend: 'Backend',
    },
    letsTalk: '¡Hablemos!',
  },

  // Playground Section
  playground: {
    title: 'Playground',
    subtitle: 'Demos interactivos y experimentos 3D en tiempo real desarrollados en Unity URP.',
    viewMore: 'Ver más demos interactivas',
  },

  // Footer
  footer: {
    developedWith: 'Desarrollado con',
    and: 'y',
  },

  // Projects Page
  projectsPage: {
    backToHome: '← Volver al inicio',
    title: 'Todos mis proyectos',
    subtitle:
      'Explora mi portfolio completo de proyectos en XR, videojuegos y desarrollo de software.',
  },

  // Project Page
  projectPage: {
    backToProjects: '← Volver a proyectos',
    platform: 'Plataforma',
    technology: 'Tecnología',
    client: 'Cliente',
    date: 'Fecha',
    location: 'Lugar',
    project: 'PROYECTO',
    techUsed: 'Tecnologías utilizadas',
    projectInMind: '¿Tienes un proyecto en mente?',
    projectInMindSubtext:
      'Ya sea que necesites un Tech Lead, CTO, o desarrollador (XR, IA, Web, Frontend o Backend), ¡hablemos!',
    viewExternalProject: 'Ver proyecto externo',
  },

  // Playground Page
  playgroundPage: {
    backToHome: '← Volver al inicio',
    title: 'Playground',
    subtitle: 'Demos interactivos, experimentos 3D y prototipos.',
    moreInfo: 'Más información',
    inDevelopment: 'En desarrollo',
    comingSoon: 'Próximamente',
    nextExperiment: 'Próximo experimento',
    viewers: {
      renderCreator: {
        title: 'Render Creator - Configurador de Interiores',
        description:
          'Configurador de baños en tiempo real desarrollado en Unity URP con optimizaciones avanzadas.',
      },
      houseNavigator: {
        title: 'Navegador 3D de Vivienda',
        description:
          'Recorrido virtual en primera persona desarrollado en Unity URP con iluminación GI bakeada.',
      },
      model3d: {
        title: 'Visor 3D Interactivo',
        description: 'Modelos 3D con animaciones y materiales PBR. Soporta GLB, GLTF, FBX.',
      },
      comingSoon: {
        title: 'Próximamente',
        description: 'Nuevos experimentos y demos en desarrollo. Mantente atento.',
      },
    },
    modal: {
      features: 'Características',
      optimization: 'Optimización Técnica',
      techStack: 'Stack Tecnológico',
    },
    bathrooms: {
      bath1: 'Baño 1',
      bath2: 'Baño 2',
      bath3: 'Baño 3',
      bath4: 'Baño 4',
    },
  },

  // Common
  common: {
    loading: 'Cargando...',
    error: 'Error',
    close: 'Cerrar',
  },
};
